<template>
  <div v-if="crumbs" class="page-header">
    <template v-for="(crumb, i) in crumbs" :key="i">
      <i v-if="i > 0" class="pi pi-angle-right page-header__sep" aria-hidden="true" />

      <BreadcrumbDropdown
        v-if="crumb.options && crumb.options.length"
        :label="crumb.label"
        :options="crumb.options"
        :current="crumb.current"
      />
      <span
        v-else
        class="page-header__text"
        :class="{ 'page-header__text--current': crumb.current }"
      >
        {{ crumb.label }}
      </span>

      <button
        v-if="crumb.onClose"
        type="button"
        class="page-header__close"
        title="Back to collection"
        aria-label="Back to collection"
        @click="crumb.onClose"
      >
        <i class="pi pi-times" aria-hidden="true" />
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BreadcrumbDropdown from './BreadcrumbDropdown.vue'

// Auto-builds the breadcrumb from nav + activePage: Context › Section › Page, with
// the Section and Page crumbs offering sibling dropdowns that emit `navigate`.
// Trailing page-published `subCrumbs` (string or { label, options?, onClose? })
// are appended; the last crumb renders as "current". Renders nothing when
// activePage isn't a page in the nav.
const props = defineProps({
  nav: { type: Object, required: true },
  activePage: { type: String, default: '' },
  subCrumbs: { type: Array, default: null },
})
const emit = defineEmits(['navigate'])

const crumbs = computed(() => {
  const section = props.nav.sections.find((s) => s.pages.some((p) => p.id === props.activePage))
  const page = section?.pages.find((p) => p.id === props.activePage)
  if (!page) return null

  const rich = (props.subCrumbs || []).map((c) => (typeof c === 'string' ? { label: c } : c))

  const sectionOptions = props.nav.sections
    .filter((s) => s.pages.length > 0)
    .map((s) => ({
      label: s.label,
      active: s.id === section.id,
      onClick: () => emit('navigate', s.pages[0].id),
    }))

  const pageOptions = section.pages.map((p) => ({
    label: p.label,
    active: p.id === props.activePage,
    onClick: () => emit('navigate', p.id),
  }))

  return [
    { label: props.nav.name },
    { label: section.label, options: sectionOptions },
    { label: page.label, options: pageOptions, current: !rich.length },
    ...rich.map((c, i) => ({ ...c, current: i === rich.length - 1 })),
  ]
})
</script>

<style scoped>
.page-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 28px;
  border-bottom: 1px solid var(--p-content-border-color);
  background: var(--p-content-background);
  position: relative;
  z-index: 200;
  font-size: 12px;
}

.page-header__text {
  font-weight: 500;
  color: var(--p-surface-400);
}

.page-header__text--current {
  font-weight: 700;
  color: var(--p-text-muted-color);
}

.page-header__sep {
  font-size: 9px;
  color: var(--p-surface-400);
  opacity: 0.5;
}

.page-header__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  margin-left: 1px;
  padding: 0;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: var(--p-surface-400);
}

.page-header__close:hover {
  background: var(--p-overlay-popover-background);
  color: var(--p-text-color);
}

.page-header__close .pi {
  font-size: 9px;
}
</style>
